import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

const SCRIPT_ID = "cloudflare-turnstile-script";
const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

type TurnstileApi = {
  render: (container: HTMLElement, options: {
    sitekey: string;
    action: string;
    appearance: "interaction-only";
    execution: "execute";
    theme: "auto";
    size: "flexible";
    "response-field": false;
    callback: (token: string) => void;
    "error-callback": () => void;
    "expired-callback": () => void;
  }) => string;
  execute: (widgetId: string) => void;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let turnstileLoader: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstileLoader) return turnstileLoader;

  turnstileLoader = new Promise<TurnstileApi>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    const script = existing ?? document.createElement("script");
    const timer = window.setTimeout(() => reject(new Error("Turnstile script timed out")), 10_000);
    const onLoad = () => {
      window.clearTimeout(timer);
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error("Turnstile API unavailable"));
    };
    const onError = () => {
      window.clearTimeout(timer);
      reject(new Error("Turnstile script failed to load"));
    };
    script.addEventListener("load", onLoad, { once: true });
    script.addEventListener("error", onError, { once: true });
    if (!existing) {
      script.id = SCRIPT_ID;
      script.src = SCRIPT_URL;
      script.async = true;
      script.defer = true;
      document.head.append(script);
    }
  }).catch((error: unknown) => {
    turnstileLoader = null;
    throw error;
  });

  return turnstileLoader;
}

export type TurnstileHandle = {
  getToken: () => Promise<string>;
};

export const TurnstileWidget = forwardRef<TurnstileHandle, { action: string; siteKey: string }>(
  function TurnstileWidget({ action, siteKey }, ref) {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    const apiRef = useRef<TurnstileApi | null>(null);
    const pendingRef = useRef<Promise<string> | null>(null);
    const resolveRef = useRef<((token: string) => void) | null>(null);
    const rejectRef = useRef<((error: Error) => void) | null>(null);

    useImperativeHandle(ref, () => ({
      getToken: () => {
        if (pendingRef.current) return pendingRef.current;
        const pending = (async () => {
          const api = await loadTurnstile();
          const container = containerRef.current;
          if (!container) throw new Error("Turnstile container unavailable");

          const token = new Promise<string>((resolve, reject) => {
            resolveRef.current = resolve;
            rejectRef.current = reject;
          });
          if (!widgetIdRef.current) {
            widgetIdRef.current = api.render(container, {
              sitekey: siteKey,
              action,
              appearance: "interaction-only",
              execution: "execute",
              theme: "auto",
              size: "flexible",
              "response-field": false,
              callback: (value) => resolveRef.current?.(value),
              "error-callback": () => rejectRef.current?.(new Error("Turnstile verification failed")),
              "expired-callback": () => rejectRef.current?.(new Error("Turnstile verification expired")),
            });
            apiRef.current = api;
          } else {
            api.reset(widgetIdRef.current);
          }
          api.execute(widgetIdRef.current);
          return token;
        })();
        pendingRef.current = pending;
        const clearPending = () => {
          if (pendingRef.current === pending) pendingRef.current = null;
          resolveRef.current = null;
          rejectRef.current = null;
        };
        void pending.then(clearPending, clearPending);
        return pending;
      },
    }), [action, siteKey]);

    useEffect(() => () => {
      rejectRef.current?.(new Error("Turnstile widget was removed"));
      if (apiRef.current && widgetIdRef.current) apiRef.current.remove(widgetIdRef.current);
    }, []);

    return <div ref={containerRef} className="turnstile-shell" aria-label="安全驗證" />;
  },
);
